// Dataset 저장 위치(storage backend) wire 계약의 경계다.
//
// Dataset 의 정본은 GCS 와 Azure Blob 중 한 곳에 있다. 저장 위치에 따라 쓸 수 있는 기능이 다르다.
// 지원표의 정본은 BE `service/storage/backend_feature.py` 다. 서버는 그 표로 요청을 409 로
// 거절한다. 이 파일은 그 표의 사본을 한 곳에 둔다. 화면은 이 파일의 판정으로 버튼을 끈다. 그래서
// 켜진 버튼이 409 가 되지 않는다. BE 표가 바뀌면 이 파일의 표를 같이 고친다.
//
// 서버가 이 화면보다 먼저 새 저장 위치를 낼 수 있다. 그래서 이 파일의 조회는 모르는 값에서 던지지
// 않는다. 모르는 위치는 모든 기능을 지원하고 GCS 의 PUT 계약을 따른다고 읽는다.
//
// Owns:
// - `DatasetStorageBackend`·`StorageFeature` wire 어휘.
// - 저장 위치가 지원하지 않는 기능의 표와 판정.
// - 저장 위치마다 다른 extra 파일 PUT 계약(`storagePutContract`).
// - 저장 위치 오류(409 `dataset.storage_backend_unsupported`, 503
//   `dataset.storage_backend_not_configured`)의 판독.
//
// Does not own:
// - 사용자 문구(`@/components/dataset/storageBackendText`).
// - 기능마다 다른 자격 조건(상태, 권한). 그 기능의 화면이 판정한다.
// - 학습 버튼의 판정. 응답의 `Dataset.training_supported` 를 읽는다.

import { isHttpClientError } from "@/http";

export const DATASET_STORAGE_BACKENDS = ["gcp_storage", "azure_blob"] as const;

export type DatasetStorageBackend = (typeof DATASET_STORAGE_BACKENDS)[number];

// 저장 위치에 따라 지원 여부가 달라지는 기능이다. 값은 409 응답의 `params.feature` 로 온다.
const STORAGE_FEATURES = ["training", "annotation", "merge", "diagnostics"] as const;

export type StorageFeature = (typeof STORAGE_FEATURES)[number];

/** URL 과 오류 응답처럼 타입이 보장되지 않는 값이 저장 위치인지 판정한다. */
export function isDatasetStorageBackend(value: unknown): value is DatasetStorageBackend {
  return DATASET_STORAGE_BACKENDS.some((backend) => backend === value);
}

function isStorageFeature(value: unknown): value is StorageFeature {
  return STORAGE_FEATURES.some((feature) => feature === value);
}

// 저장 위치가 지원하지 않는 기능이다. 표에 없는 위치는 모든 기능을 지원한다.
const UNSUPPORTED_FEATURES: Partial<Record<DatasetStorageBackend, readonly StorageFeature[]>> = {
  azure_blob: ["training", "annotation", "merge"],
};

/**
 * 저장 위치가 지원하지 않는 기능을 돌려준다.
 *
 * Args:
 *   backend: Dataset 의 저장 위치. 예) `"azure_blob"`
 *
 * Returns:
 *   지원하지 않는 기능. 예) `"azure_blob"` → `["training", "annotation", "merge"]`,
 *   `"gcp_storage"` → `[]`
 */
export function unsupportedStorageFeatures(
  backend: DatasetStorageBackend,
): readonly StorageFeature[] {
  return UNSUPPORTED_FEATURES[backend] ?? [];
}

/**
 * 저장 위치가 기능을 지원하는지 판정한다.
 *
 * Args:
 *   backend: Dataset 의 저장 위치. 예) `"azure_blob"`
 *   feature: 판정할 기능. 예) `"annotation"`
 *
 * Returns:
 *   지원하면 true. 예) `("azure_blob", "annotation")` → false, `("gcp_storage", "annotation")` → true
 */
export function supportsStorageFeature(
  backend: DatasetStorageBackend,
  feature: StorageFeature,
): boolean {
  return !unsupportedStorageFeatures(backend).includes(feature);
}

/**
 * 그 기능을 지원하는 저장 위치가 하나뿐이면 그 위치를 돌려준다.
 *
 * 후보 목록 요청의 `storage_backend` 게이트 값이다. 고른 뒤에 409 로 거절될 Dataset 을 목록에서 뺀다.
 *
 * Args:
 *   feature: 후보를 쓸 기능. 예) `"training"`
 *
 * Returns:
 *   지원하는 위치가 하나면 그 위치. 예) `"training"` → `"gcp_storage"`. 모든 위치가 지원하거나
 *   둘 이상이 지원하면 null 이다. 게이트는 값 하나만 받는다. 그때는 목록을 좁히지 않는다.
 */
export function storageBackendGateFor(feature: StorageFeature): DatasetStorageBackend | null {
  const supporting = DATASET_STORAGE_BACKENDS.filter((backend) =>
    supportsStorageFeature(backend, feature),
  );
  return supporting.length === 1 ? supporting[0] : null;
}

// 저장 위치 오류에서 읽은 값이다. 모르는 값은 null 이다(이 화면보다 새 서버).
type StorageBackendError =
  | {
      kind: "unsupported";
      storageBackend: DatasetStorageBackend | null;
      feature: StorageFeature | null;
    }
  | { kind: "not_configured"; storageBackend: DatasetStorageBackend | null };

/**
 * 저장 위치 오류를 읽는다.
 *
 * - 409 `dataset.storage_backend_unsupported`: 저장 위치가 요청한 기능을 지원하지 않는다.
 *   `params` 는 `{storage_backend, feature, dataset_uuids}` 다.
 * - 503 `dataset.storage_backend_not_configured`: 서버에 그 저장 위치의 설정이 없다.
 *   `params` 는 `{storage_backend}` 다.
 *
 * Returns:
 *   다른 오류면 null. 예) Azure Dataset 의 Training 제출 →
 *   `{ kind: "unsupported", storageBackend: "azure_blob", feature: "training" }`
 */
export function readStorageBackendError(error: unknown): StorageBackendError | null {
  if (!isHttpClientError(error)) {
    return null;
  }
  const unsupported = error.code === "dataset.storage_backend_unsupported";
  if (!unsupported && error.code !== "dataset.storage_backend_not_configured") {
    return null;
  }
  const params = readParams(error.details);
  const storageBackend = isDatasetStorageBackend(params.storage_backend)
    ? params.storage_backend
    : null;
  if (!unsupported) {
    return { kind: "not_configured", storageBackend };
  }
  return {
    kind: "unsupported",
    storageBackend,
    feature: isStorageFeature(params.feature) ? params.feature : null,
  };
}

function readParams(details: unknown): Record<string, unknown> {
  if (typeof details !== "object" || details === null || !("params" in details)) {
    return {};
  }
  const params = details.params;
  return typeof params === "object" && params !== null ? (params as Record<string, unknown>) : {};
}

// 저장 위치마다 다른 extra 파일 PUT 계약이다. 정본은 BE `docs/specs/extra-artifact-files.md` 다.
type StoragePutContract = {
  // 새 경로에 object 가 이미 있을 때 storage 가 PUT 을 거절하는 status.
  existsStatus: number;
  // 그 status 가 다른 사유와 겹치는지. Azure 는 만료된 서명도 403 이다. 겹치면 화면이 사유를 단정하지
  // 않는다.
  existsStatusAmbiguous: boolean;
  // PUT 한 번으로 올릴 수 있는 크기의 상한(바이트). null 은 화면이 막을 상한이 없다는 뜻이다.
  maxFileBytes: number | null;
};

const PUT_CONTRACTS: Record<DatasetStorageBackend, StoragePutContract> = {
  gcp_storage: { existsStatus: 412, existsStatusAmbiguous: false, maxFileBytes: null },
  // Azure 의 Put Blob 한 번은 5,000 MiB 까지다. 더 큰 파일은 413 으로 거절된다.
  azure_blob: { existsStatus: 403, existsStatusAmbiguous: true, maxFileBytes: 5000 * 1024 * 1024 },
};

/**
 * 저장 위치의 extra 파일 PUT 계약을 돌려준다.
 *
 * Args:
 *   backend: Dataset 의 저장 위치. 모르면 undefined.
 *
 * Returns:
 *   예) `"azure_blob"` → `{ existsStatus: 403, existsStatusAmbiguous: true, maxFileBytes: 5_242_880_000 }`.
 *   저장 위치를 모르거나 이 화면이 모르는 값이면 GCS 의 계약이다.
 */
export function storagePutContract(backend: string | undefined): StoragePutContract {
  return isDatasetStorageBackend(backend) ? PUT_CONTRACTS[backend] : PUT_CONTRACTS.gcp_storage;
}
